import { store } from "../redux/store";
import { logoutUser } from "../redux/userSlice";

// Prefer explicit API base URL from environment, but
// fall back to same-origin `/api` which is what Nginx
// proxies to the backend in production.
const BASE_URL = process.env.REACT_APP_SERVER_URL || "/api";

// console.log("BASE_URL", BASE_URL);
// console.log("process.env.REACT_APP_SERVER_URL", process.env.REACT_APP_SERVER_URL);

if (!BASE_URL) {
  // Optional: warn during development if the base URL is not set
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_SERVER_URL is not defined. API requests may fail.");
}


/**
 * Generic API helper to make requests with httpOnly cookies.
 * Cookies are automatically sent by the browser when credentials: 'include' is set.
 *
 * @param {string} endpoint - API endpoint path, e.g. "/api/products/get-all"
 * @param {Object} options - fetch options (method, headers, body, etc.)
 * @param {boolean} [withAuth=true] - whether to include credentials (cookies) in request
 */
export async function apiRequest(endpoint, options = {}, withAuth = true) {
  if (!BASE_URL) {
    throw new Error("REACT_APP_SERVER_URL is not configured");
  }

  const url = `${BASE_URL}${endpoint}`;

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: withAuth ? "include" : "same-origin", // Include cookies for authenticated requests
    });

    // Handle network errors (CORS, connection refused, etc.)
    if (!response) {
      throw new Error("Network error: Unable to connect to server. Please check your connection.");
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      // ✅ Global 401 handler - automatically handle expired/invalid tokens
      if (response.status === 401) {
        // Clear user data from Redux (Redux Persist will automatically clear localStorage)
        store.dispatch(logoutUser());
        localStorage.removeItem("items");
        // Redirect to login page
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        throw new Error("Session expired. Please log in again.");
      }

      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = response.headers.get("Retry-After");
        const message = retryAfter
          ? `Too many requests. Please try again after ${retryAfter} seconds.`
          : "Too many requests. Please try again later.";
        throw new Error(message);
      }

      // Extract error message from response
      const message =
        (data && (data.message || data.error)) ||
        response.statusText ||
        `Request failed with status ${response.status}`;
      
      const error = new Error(message);
      // Attach full error data for better error handling
      error.data = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    // If error already has a message (from our error handling above), re-throw it
    if (error.message && !error.message.includes("fetch")) {
      throw error;
    }

    // Handle network errors (fetch fails completely - connection refused, CORS, etc.)
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new Error(
        "Network error: Unable to connect to server. Please check if the server is running and your connection."
      );
    }

    // Handle other network-related errors
    if (error instanceof TypeError || error.name === "NetworkError") {
      throw new Error(
        "Network error: Unable to connect to server. Please check your connection and try again."
      );
    }

    // Re-throw other errors as-is
    throw error;
  }
}